import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Link } from "@/components/Link";
import { useRouteParams } from "@/hooks/useRouteParams";
import {
  MapPin,
  Instagram,
  Share2,
  Clock,
  Users,
  Edit,
  Save,
  X,
  Trash2,
} from "lucide-react";
import StorePageHeader from "@/components/StorePageHeader";
import ClaimBanner from "@/components/ClaimBanner";
import RecentReviewsGrid from "@/components/RecentReviewsGrid";
import AboutStore from "@/components/AboutStore";
import ProductSamplesSlider from "@/components/ProductSamplesSlider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ReviewsSection from "@/components/ReviewsSection";
import { BlogTrustLink } from "@/components/blog/BlogTrustLink";
import StoreSectionNav from "@/components/StoreSectionNav";
import { fetchStore, getSimilarStores, type Store as StoreType } from "@/api/storeService";
import { StoreListCard } from "@/components/StoreListCard";
import { updateStore, uploadStoreImage, deleteStore } from "@/api/adminService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@/hooks/useNavigate";
import ReviewsSummary, { PlatformRating } from "@/components/ReviewsSummary";
import StoreReviewSummary from "@/components/StoreReviewSummary";
import StoreLocationCard from "@/components/StoreLocationCard";
import StoreBranchesCard from "@/components/StoreBranchesCard";
import { useAuth } from "@/contexts/AuthContext";
import { useStoreEditMode } from "@/hooks/useStoreEditMode";
import { EditableText, EditableToggle, EditableTags, EditablePlatform, EditableNumber } from "@/components/admin/EditableFields";
import { EditableCategories } from "@/components/admin/EditableCategories";
import GooglePlaceEditor from "@/components/admin/GooglePlaceEditor";
import { ReviewChrome, ShortcutHelpDialog } from "@/components/admin/ReviewChrome";
import { ScraperRunDrawer } from "@/components/admin/ScraperRunDrawer";
import { FieldFlag } from "@/components/admin/FieldFlag";
import { useDraftReviewQueue } from "@/hooks/useDraftReviewQueue";
import { useReviewKeyboard } from "@/hooks/useReviewKeyboard";
import { validateDraft } from "@/lib/draftValidation";
import { getScrapeJobForStore } from "@/api/adminService";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { storeEventProps } from "@/lib/posthog";
import { toast } from "sonner";
import type { SSRReviewsData, StoreData } from "@/pages/store/@id/+data";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";
import { writeLastViewedStore } from "@/lib/lastViewedStore";
import OnboardingReturnBanner from "@/components/onboarding/OnboardingReturnBanner";

const Store = ({
  initialStore,
  initialReviewsData,
}: {
  initialStore?: StoreData | null;
  initialReviewsData?: SSRReviewsData | null;
} = {}) => {
  const { id } = useRouteParams<{ id: string }>();
  const { user, showAuthModal } = useAuth();

  const {
    data: store,
    isLoading,
    error: queryError,
    refetch: loadStore,
  } = useQuery({
    queryKey: ["store", id],
    queryFn: async () => {
      if (!id) {
        throw new Error("معرف المتجر مطلوب");
      }
      const response = await fetchStore({ id });
      return response.data;
    },
    // D-01: seed the query cache with the SSR-fetched store so isLoading is false on the
    // first render on BOTH server and client (no hydration mismatch). StoreData↔StoreType
    // type friction (RESEARCH A5) resolved by an explicit cast at this boundary — the shapes
    // overlap structurally; the SSR payload is a subset of StoreType used for first paint.
    initialData: (initialStore as StoreType | undefined) ?? undefined,
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!id,
  });

  const navigate = useNavigate();

  // Edit mode functionality (admin only)
  const editMode = useStoreEditMode({
    initialStore: store || {} as StoreType,
    onSaveSuccess: (updatedStore) => {
      // If the slug changed, navigate to the new URL so the cache key stays in sync
      if (updatedStore.slug && updatedStore.slug !== id) {
        navigate(`/store/${updatedStore.slug}`);
      }
    },
  });

  const isAdmin = user?.isAdmin;
  // The moderator Drafts preview iframes this page (45-32); inside it the page must look exactly
  // like the public page, so every admin-gated render and behaviour reads `adminUi`, never
  // `isAdmin`. Set in an effect (not at render) so SSR and hydration agree on `false`.
  const [embedded, setEmbedded] = useState(false);
  useEffect(() => {
    setEmbedded(isEmbeddedPreview());
  }, []);
  const adminUi = !!isAdmin && !embedded;
  const isReviewMode = adminUi && store?.status === "draft";
  const queryClient = useQueryClient();

  // quick-260815-dhs: similar-stores experiment — client-only fetch (deliberately NOT in
  // +data.ts), rendered as a mobile-only rail after the reviews section (view mode only).
  const { data: similarStoresData } = useQuery({
    queryKey: ["similarStores", store?._id],
    queryFn: () => getSimilarStores({ id: store!._id }),
    enabled: Boolean(store?._id) && !editMode.isEditMode,
    staleTime: 1000 * 60 * 5,
  });
  const similarStores = similarStoresData ?? [];

  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Claim banner dismissal — SSR-safe localStorage read
  const [claimDismissed, setClaimDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(`ys-claim-dismissed-${store?.slug}`) === "1";
  });
  const [scraperOpen, setScraperOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [ratingSaving, setRatingSaving] = useState(false);
  const ratingTimeoutRef = useRef<number | null>(null);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const reviewsSectionRef = useRef<HTMLDivElement>(null);

  // Draft queue (only fetch when admin viewing a draft)
  const queue = useDraftReviewQueue({
    currentId: store?._id,
    enabled: isReviewMode,
  });

  // Scrape job for the scraper drawer
  const { data: scrapeJobResponse, isLoading: scrapeJobLoading } = useQuery({
    queryKey: ["store-scrape-job", store?._id],
    queryFn: () => getScrapeJobForStore({ storeId: store!._id }),
    enabled: isReviewMode && !!store?._id,
    staleTime: 1000 * 60 * 5,
  });
  const scrapeJob = scrapeJobResponse?.ok ? scrapeJobResponse.data : null;

  const validation = useMemo(() => validateDraft(store), [store]);
  const platforms = store?.platforms ?? {};

  const { trackStore, trackReview } = usePostHogTracking();
  const storeTrackingProps = useMemo(() => storeEventProps(store), [store]);

  // Fire store_viewed once per store id (survives refetches of the same store)
  const viewedStoreIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!store?._id || viewedStoreIdRef.current === store._id) return;
    // An in-PWA moderator preview is not a public view.
    if (isEmbeddedPreview()) return;
    viewedStoreIdRef.current = store._id;
    trackStore.viewed(store._id, store.name, {
      ...storeTrackingProps,
      rating: store.rating,
      total_reviews: store.totalReviews,
    });
    // D-21: remember the store for a later context-free signup. Client-only (inside an effect,
    // after the embedded-preview return); the helper validates the slug and guards storage.
    if (!user) writeLastViewedStore(store.slug);
  }, [store, storeTrackingProps, trackStore, user]);

  const goToDraft = useCallback(
    (ref: { _id: string; slug: string } | null) => {
      if (!ref) return;
      navigate(`/store/${ref.slug || ref._id}`);
    },
    [navigate],
  );

  const handlePublishAndNext = useCallback(async () => {
    if (!store) return;
    setIsPublishing(true);
    try {
      const result = await updateStore({
        storeId: store._id,
        storeData: { status: "published" },
      });
      if (result.ok) {
        toast.success("Published", { description: store.name });
        void queryClient.invalidateQueries({ queryKey: ["draft-store-list"] });
        const nextRef = queue.next;
        if (nextRef) {
          navigate(`/store/${nextRef.slug || nextRef._id}`);
        } else {
          toast("No more drafts");
          navigate("/admin?tab=stores");
        }
      } else {
        toast.error("Failed to publish", { description: result.error });
      }
    } finally {
      setIsPublishing(false);
    }
  }, [store, queue.next, navigate, queryClient, toast]);

  const handleRatingChange = useCallback(
    (value: number) => {
      if (!store) return;
      // Update local edit-mode store immediately so UI reflects change
      if (editMode.isEditMode) {
        editMode.updateField("yetfarragRating", value);
        return;
      }
      if (ratingTimeoutRef.current) {
        window.clearTimeout(ratingTimeoutRef.current);
      }
      ratingTimeoutRef.current = window.setTimeout(async () => {
        setRatingSaving(true);
        const result = await updateStore({
          storeId: store._id,
          storeData: { yetfarragRating: value },
        });
        if (result.ok) {
          void queryClient.invalidateQueries({ queryKey: ["store", id] });
          void queryClient.invalidateQueries({ queryKey: ["draft-store-list"] });
        } else {
          toast.error("Failed to update rating", { description: result.error });
        }
        setRatingSaving(false);
      }, 450);
    },
    [store, editMode, id, queryClient, toast],
  );

  useEffect(() => {
    return () => {
      if (ratingTimeoutRef.current) {
        window.clearTimeout(ratingTimeoutRef.current);
      }
    };
  }, []);

  const handleDeleteFromChrome = useCallback(async () => {
    if (!store) return;
    if (!window.confirm(`Delete "${store.name}"? This cannot be undone.`)) return;
    const deleteGoogleMapsReviews = window.confirm(
      `Delete all Google Maps reviews for "${store.name}" as well?\n\nChoose OK to delete them too, or Cancel to keep them.`
    );
    setIsDeleting(true);
    try {
      const result = await deleteStore({ storeId: store._id, deleteGoogleMapsReviews });
      if (result.ok) {
        toast.success(result.data?.message || "Deleted");
        void queryClient.invalidateQueries({ queryKey: ["draft-store-list"] });
        const target = queue.next || queue.prev;
        if (target) {
          navigate(`/store/${target.slug || target._id}`);
        } else {
          navigate("/admin?tab=stores");
        }
      } else {
        toast.error("Failed to delete", { description: result.error });
      }
    } finally {
      setIsDeleting(false);
    }
  }, [store, queue.next, queue.prev, navigate, queryClient, toast]);

  useReviewKeyboard({
    enabled: isReviewMode && !helpOpen,
    onPrev: () => goToDraft(queue.prev),
    onNext: () => goToDraft(queue.next),
    onPublish: () => void handlePublish(),
    onPublishAndNext: () => void handlePublishAndNext(),
    onToggleEdit: () =>
      editMode.isEditMode ? editMode.cancelEdit() : editMode.enableEditMode(),
    onToggleScraper: () => setScraperOpen((v) => !v),
    onShowHelp: () => setHelpOpen(true),
    onDelete: () => void handleDeleteFromChrome(),
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !store) return;
    setIsUploadingLogo(true);
    try {
      const result = await uploadStoreImage({ storeId: store._id, type: "logo", file });
      if (result.ok && result.data) {
        editMode.updateField("logoUrl", result.data.imageUrl);
      }
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handlePublish = async () => {
    if (!store) return;
    setIsPublishing(true);
    try {
      const result = await updateStore({ storeId: store._id, storeData: { status: 'published' } });
      if (result.ok) {
        loadStore();
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async () => {
    if (!store) return;
    const deleteGoogleMapsReviews = window.confirm(
      `Delete all Google Maps reviews for "${store.name}" as well?\n\nChoose OK to delete them too, or Cancel to keep them.`
    );
    setIsDeleting(true);
    try {
      const result = await deleteStore({ storeId: store._id, deleteGoogleMapsReviews });
      if (result.ok) {
        navigate("/admin");
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const error =
    queryError instanceof Error
      ? queryError.message
      : queryError
      ? "حدث خطأ غير متوقع"
      : null;

  const handleShare = async () => {
    if (!store) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: store.name,
          text: store.description || `تحقق من متجر ${store.name} على يتفرج`,
          url: window.location.href,
        });
        trackStore.shared({
          method: "native",
          surface: "store_header",
          ...storeTrackingProps,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      // Fallback to copying URL
      navigator.clipboard.writeText(window.location.href);
      trackStore.shared({
        method: "clipboard",
        surface: "store_header",
        ...storeTrackingProps,
      });
      // You could add a toast notification here
    }
  };

  // D-01 belt-and-suspenders: with initialData set, isLoading is already false on the seeded
  // SSR/first-client render, so the skeleton never suppresses the SSR'd body.
  if (isLoading && !initialStore) {
    return (
      <div className="min-h-screen bg-white font-arabic">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="bg-gray-200 h-8 w-64 rounded mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="bg-gray-200 h-96 rounded-lg mb-6"></div>
                <div className="space-y-4">
                  <div className="bg-gray-200 h-6 rounded"></div>
                  <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                  <div className="bg-gray-200 h-4 rounded w-1/2"></div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-gray-200 h-32 rounded-lg"></div>
                <div className="bg-gray-200 h-24 rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer variant="minimal" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white font-arabic">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-red-600 font-arabic text-lg mb-4">{error}</p>
            <div className="space-x-4">
              <Button
                onClick={() => loadStore()}
                className="bg-warmOrange-500 hover:bg-warmOrange-600"
              >
                إعادة المحاولة
              </Button>
              <Link to="/stores">
                <Button variant="outline">العودة للمتاجر</Button>
              </Link>
            </div>
          </div>
        </div>
        <Footer variant="minimal" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-white font-arabic">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <p className="text-gray-600 font-arabic text-lg mb-4">
              المتجر غير موجود
            </p>
            <Link to="/stores">
              <Button className="bg-warmOrange-500 hover:bg-warmOrange-600">
                العودة للمتاجر
              </Button>
            </Link>
          </div>
        </div>
        <Footer variant="minimal" />
      </div>
    );
  }

  // The page's review CTA path, shared by the header CTA and the onboarding return banner (49-16).
  // Guests are prompted to sign in instead of being silently scrolled. Callers fire their own event.
  const openReviewPath = () => {
    if (!user) {
      showAuthModal({ trigger: "write_review", storeSlug: store.slug });
      return;
    }
    reviewsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white font-arabic">
      {isReviewMode ? (
        <ReviewChrome
          storeName={store.name}
          storeSlugOrId={store.slug || store._id}
          isEditMode={editMode.isEditMode}
          hasChanges={editMode.hasChanges}
          isSaving={editMode.isLoading}
          onEnableEdit={editMode.enableEditMode}
          onSaveEdit={editMode.saveChanges}
          onCancelEdit={editMode.cancelEdit}
          onOpenScraper={() => setScraperOpen(true)}
          currentIndex={queue.currentIndex}
          total={queue.total}
          prevId={queue.prev?._id ?? null}
          nextId={queue.next?._id ?? null}
          onPrev={() => goToDraft(queue.prev)}
          onNext={() => goToDraft(queue.next)}
          validation={validation}
          rating={
            editMode.isEditMode
              ? editMode.editedStore.yetfarragRating ?? 0
              : store.yetfarragRating ?? 0
          }
          onRatingChange={handleRatingChange}
          ratingSaving={ratingSaving}
          ratingNeedsAttention={!!validation.flags.rating}
          onDelete={handleDeleteFromChrome}
          onPublish={handlePublish}
          onPublishAndNext={handlePublishAndNext}
          publishing={isPublishing}
          deleting={isDeleting}
          onShowHelp={() => setHelpOpen(true)}
        />
      ) : (
        <Header />
      )}

      <ScraperRunDrawer
        open={scraperOpen}
        onOpenChange={setScraperOpen}
        scrapeJob={scrapeJob}
        loading={scrapeJobLoading}
        storeSlug={store.slug}
        storeId={store._id}
      />
      <ShortcutHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />

      {/* ── Onboarding return banner (49-16, UI-SPEC S4): WhatsApp variant B arrivals only, client
           side only, never in the embedded preview. Renders nothing (and no wrapper) otherwise ── */}
      {!editMode.isEditMode && (
        <OnboardingReturnBanner
          store={store}
          onWriteReview={openReviewPath}
          wrapperClassName="mx-auto max-w-[1400px] px-4 pt-4 lg:px-10"
        />
      )}

      {/* Store identity header — full width, outside the container */}
      <StorePageHeader
        store={editMode.isEditMode ? { ...store, ...editMode.editedStore } : store}
        isEditMode={editMode.isEditMode && adminUi}
        logoRef={logoInputRef}
        onLogoUpload={handleLogoUpload}
        isUploadingLogo={isUploadingLogo}
        onScrollToReviews={() => {
          trackReview.ctaClicked({
            surface: "header_cta",
            authenticated: !!user,
            ...storeTrackingProps,
          });
          openReviewPath();
        }}
      />

      {/* ── Claim Banner — non-edit-mode, unverified stores only ── */}
      {!editMode.isEditMode && !store.verified && !claimDismissed && (
        <div className="mx-auto max-w-[1400px] px-4 pt-4 lg:px-10">
          <ClaimBanner
            storeSlug={store.slug}
            onDismiss={() => {
              if (typeof window !== "undefined") {
                localStorage.setItem(`ys-claim-dismissed-${store.slug}`, "1");
              }
              setClaimDismissed(true);
            }}
          />
        </div>
      )}

      {/* Mobile-only sticky section nav — sits below the 54px site header (quick-260815-dhs) */}
      {!editMode.isEditMode && (
        <StoreSectionNav hasSimilarStores={similarStores.length > 0} />
      )}

      {/* Draft banner — shown to admin regardless of edit mode */}
      {store.status === 'draft' && adminUi && !isReviewMode && (
        <div className="mx-auto max-w-[1400px] px-4 pt-3 lg:px-10">
          <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-300 flex items-center justify-between">
            <span className="text-sm font-medium text-yellow-800">
              This store is a <strong>draft</strong> and is not visible to users.
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handlePublish}
                disabled={isPublishing}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isPublishing ? "جاري النشر..." : "نشر المتجر"}
              </Button>
              <Link to="/admin">
                <Button size="sm" variant="outline" className="text-yellow-800 border-yellow-400 hover:bg-yellow-100">
                  Back to Admin
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ── Admin edit entry — non-edit-mode, non-review-mode admins only ── */}
      {adminUi && !editMode.isEditMode && !isReviewMode && (
        <div className="mx-auto max-w-[1400px] px-4 pt-3 lg:px-10">
          <div className="flex items-center justify-end">
            <Button
              onClick={editMode.enableEditMode}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              تعديل
            </Button>
          </div>
        </div>
      )}

      {/* ── AI Review Summary — non-edit-mode, when a summary exists ── */}
      {!editMode.isEditMode && store.reviewSummary?.text && (
        <div id="overview" className="scroll-mt-28" style={{ padding: "24px 0", marginTop: 16 }}>
          <div className="mx-auto max-w-[1400px] px-4 lg:px-10">
            <StoreReviewSummary summary={store.reviewSummary} />
          </div>
        </div>
      )}

      {/* ── Recent Reviews Grid — non-edit-mode, when reviews exist. lg-only: the mobile
           page gets the full ReviewsSection below instead (quick-260815-dhs) ── */}
      {!editMode.isEditMode && (initialReviewsData?.reviews?.length ?? 0) > 0 && (
        <div className="hidden lg:block" style={{ padding: "24px 0", marginTop: 16 }}>
          <div className="mx-auto max-w-[1400px] px-4 lg:px-10">
            <RecentReviewsGrid
              reviews={(initialReviewsData?.reviews ?? []).slice(0, 3)}
              totalCount={initialReviewsData?.pagination?.totalItems ?? store.totalReviews}
              onScrollToAllReviews={() => {
                trackReview.ctaClicked({
                  surface: "recent_grid",
                  authenticated: !!user,
                  ...storeTrackingProps,
                });
                reviewsSectionRef.current?.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </div>
        </div>
      )}

      {/* ── About Store + Quick Details — non-edit-mode. Carries the overview anchor when
           there is no AI summary section so the نظرة عامة chip always has a target ── */}
      {!editMode.isEditMode && (
        <div
          id={store.reviewSummary?.text ? undefined : "overview"}
          className="mx-auto max-w-[1400px] scroll-mt-28 px-4 py-6 lg:px-10 lg:py-12"
        >
          <AboutStore store={store} />
        </div>
      )}

      {/* ── Branches (BRNCH-01) are rendered inside AboutStore's Quick Details panel as a single
           «الفروع» count row that opens a sheet — see StoreBranchesQuickGlance. A full-width card
           of expanded rows read badly on both breakpoints. The always-open StoreBranchesCard is
           still mounted in the edit-mode admin grid below. ── */}

      {/* ── Product Samples Slider — non-edit-mode ── */}
      {!editMode.isEditMode && (store.topProducts?.length ?? 0) > 0 && (
        <div style={{ background: "var(--ys-bg)", padding: "0 0 48px 0" }}>
          <div className="mx-auto max-w-[1400px] px-4 lg:px-10">
            <ProductSamplesSlider
              products={store.topProducts!}
              fallbackUrl={platforms.website}
              trackingProps={storeTrackingProps}
            />
          </div>
        </div>
      )}

      {/* ── Edit-mode 2-col admin grid ── */}
      {editMode.isEditMode && (
      <div className="container mx-auto px-4 py-8">
        {/* edit mode placeholder — draft banner already rendered above */}
        <div style={{ height: 0 }} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Store Details */}
            <div className="space-y-6">
              {/* Admin controls + field flags row */}
              <div className="flex items-start justify-between">
                <div>
                  {editMode.isEditMode ? (
                    <EditableText
                      value={editMode.editedStore.name || ""}
                      onSave={(value) => editMode.updateField("name", value)}
                      placeholder="اسم المتجر"
                      className="text-2xl font-bold text-gray-900 font-arabic"
                      disabled={editMode.isLoading}
                    />
                  ) : (
                    isReviewMode && (
                      <div className="flex items-center gap-1 text-gray-500 text-sm">
                        {store.name}
                        <FieldFlag issue={validation.flags.name} />
                        <FieldFlag issue={validation.flags.logo} />
                      </div>
                    )
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {adminUi && (
                    <>
                      {editMode.isEditMode ? (
                        <div className="flex gap-2">
                          <Button
                            onClick={editMode.saveChanges}
                            disabled={!editMode.hasChanges || editMode.isLoading}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            {editMode.isLoading ? "جاري الحفظ..." : "حفظ"}
                          </Button>
                          <Button
                            onClick={editMode.cancelEdit}
                            disabled={editMode.isLoading}
                            variant="outline"
                            size="sm"
                          >
                            <X className="w-4 h-4 mr-1" />
                            إلغاء
                          </Button>
                          {!showDeleteConfirm ? (
                            <Button
                              onClick={() => setShowDeleteConfirm(true)}
                              disabled={editMode.isLoading}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              حذف
                            </Button>
                          ) : (
                            <div className="flex gap-1 items-center">
                              <Button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                size="sm"
                                className="bg-red-600 hover:bg-red-700 text-white"
                              >
                                {isDeleting ? "جاري الحذف..." : "تأكيد الحذف"}
                              </Button>
                              <Button
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={isDeleting}
                                variant="outline"
                                size="sm"
                              >
                                لا
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Button
                          onClick={editMode.enableEditMode}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          تعديل
                        </Button>
                      )}
                    </>
                  )}

                  <Button
                    onClick={handleShare}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" />
                    مشاركة
                  </Button>
                </div>
              </div>

              {/* Location (edit mode only — view mode shown in header) */}
              {editMode.isEditMode && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5" />
                  <EditableText
                    value={editMode.editedStore.location || ""}
                    onSave={(value) => editMode.updateField("location", value)}
                    placeholder="موقع المتجر"
                    className="font-arabic"
                    disabled={editMode.isLoading}
                  />
                  {isReviewMode && <FieldFlag issue={validation.flags.location} />}
                </div>
              )}
              {/* Location flag in review mode (view) */}
              {!editMode.isEditMode && isReviewMode && store.location === undefined && (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4" />
                  <span>—</span>
                  <FieldFlag issue={validation.flags.location} />
                </div>
              )}

              {(store.description || editMode.isEditMode || isReviewMode) && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 font-arabic mb-3">
                    عن المتجر
                    {isReviewMode && <FieldFlag issue={validation.flags.description} />}
                  </h2>
                  {editMode.isEditMode ? (
                    <EditableText
                      value={editMode.editedStore.description || ""}
                      onSave={(value) => editMode.updateField("description", value)}
                      placeholder="وصف المتجر"
                      className="text-gray-700 font-arabic leading-relaxed"
                      multiline
                      disabled={editMode.isLoading}
                    />
                  ) : (
                    <p className="text-gray-700 font-arabic leading-relaxed">
                      {store.description}
                    </p>
                  )}
                  {store.descriptionEn && !editMode.isEditMode && (
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {store.descriptionEn}
                    </p>
                  )}
                </div>
              )}

              {/* All Categories */}
              {((store.categories && store.categories.length > 0) || editMode.isEditMode || isReviewMode) && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 font-arabic mb-3">
                    الفئات المتعلقة
                    {isReviewMode && <FieldFlag issue={validation.flags.categories} />}
                  </h2>
                  {editMode.isEditMode ? (
                    <EditableCategories
                      categories={editMode.editedStore.categories || []}
                      prominentCategory={editMode.editedStore.prominentCategory}
                      onSave={(data) => {
                        editMode.updateField("categories", data.categories);
                        editMode.updateField("prominentCategory", data.prominentCategory);
                      }}
                      disabled={editMode.isLoading}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {store.categories?.map((cat, index) => (
                        <Link
                          key={index}
                          to={`/category/${cat.category.slug}`}
                          className="transition-transform hover:scale-105"
                        >
                          <span
                            className={`inline-block rounded-full px-3 py-2 text-sm font-medium border cursor-pointer hover:shadow-md transition-all ${
                              cat.categoryId === store.prominentCategory?.categoryId
                                ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 border-blue-200 hover:from-blue-200 hover:to-purple-200'
                                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                            }`}
                          >
                            {cat.category.title.ar}
                            <span className="ml-2 opacity-70 text-xs bg-white/50 px-1 rounded">
                              {cat.relevanceScore}/10
                            </span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {((store.tags && store.tags.length > 0) || editMode.isEditMode || isReviewMode) && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 font-arabic mb-3">
                    العلامات
                    {isReviewMode && <FieldFlag issue={validation.flags.tags} />}
                  </h2>
                  {editMode.isEditMode ? (
                    <EditableTags
                      tags={editMode.editedStore.tags || []}
                      onSave={(tags) => editMode.updateField("tags", tags)}
                      disabled={editMode.isLoading}
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {/* Plain text on purpose: tag search was removed from /stores on 2026-07-19
                          (quick-260719-fac), so a chip link only minted crawlable
                          /stores?tags= duplicates that now return 410. */}
                      {store.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-block bg-orange-50 text-orange-700 rounded-full px-3 py-2 text-sm font-medium border border-orange-200"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Admin Controls */}
              {editMode.isEditMode && adminUi && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 font-arabic mb-3">
                    إعدادات الإدارة
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <EditableToggle
                      value={editMode.editedStore.featured || false}
                      onSave={(value) => editMode.updateField("featured", value)}
                      label="متجر مميز"
                      disabled={editMode.isLoading}
                    />
                    <EditableToggle
                      value={editMode.editedStore.verified || false}
                      onSave={(value) => editMode.updateField("verified", value)}
                      label="متجر موثق"
                      disabled={editMode.isLoading}
                    />
                  </div>
                  <div className="mt-4">
                    <EditableNumber
                      value={editMode.editedStore.yetfarragRating ?? 0}
                      onSave={(value) => editMode.updateField("yetfarragRating", value)}
                      label="تقييم يتفرج الداخلي (0-10)"
                      min={0}
                      max={10}
                      step={0.5}
                      disabled={editMode.isLoading}
                      helperText="تقييم داخلي للترتيب - غير مرئي للمستخدمين. القيمة الأعلى تظهر أولاً في نتائج البحث."
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Slug (URL identifier)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      يستخدم في رابط المتجر: /store/<span className="font-mono">{editMode.editedStore.slug}</span>
                    </p>
                    <EditableText
                      value={editMode.editedStore.slug || ""}
                      onSave={(value) => editMode.updateField("slug", value.trim().toLowerCase().replace(/\s+/g, "-"))}
                      placeholder="store-slug"
                      className="font-mono text-sm"
                      disabled={editMode.isLoading}
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">مكان Google</h3>
                    <GooglePlaceEditor
                      storeId={store._id}
                      currentPlace={store.googlePlaces}
                      onDone={() => void loadStore()}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* Store Info */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 font-arabic mb-4">
                  معلومات المتجر
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium font-arabic">تاريخ الانضمام</p>
                      <p className="text-sm text-gray-600">
                        {new Date(store.createdAt).toLocaleDateString("ar-EG")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium font-arabic">عدد التقييمات</p>
                      <p className="text-sm text-gray-600">
                        {store.totalReviews ?? 0} تقييم
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Store Card */}
            <ReviewsSummary
              storeId={store._id}
              storeName={store.name}
              categorySlug={store?.category?.slug}
              googlePlaces={store.googlePlaces}
              platformRating={store.metadata?.platformRating as PlatformRating | undefined}
            />

            {/* Location Card */}
            <StoreLocationCard
              storeName={store.name}
              googlePlaces={store.googlePlaces}
              basicLocation={store.location}
              trackingProps={storeTrackingProps}
            />

            {/* Branches Card */}
            <StoreBranchesCard branches={store.branches} trackingProps={storeTrackingProps} />

            {/* Contact Card — edit mode only; view-mode contacts are in the page header */}
            {editMode.isEditMode && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 font-arabic mb-4">
                    تواصل مع المتجر
                    {isReviewMode && <FieldFlag issue={validation.flags.platforms} />}
                  </h3>
                  <EditablePlatform
                    platforms={editMode.editedStore.platforms || {}}
                    onSave={(p) => editMode.updateField("platforms", p)}
                    disabled={editMode.isLoading}
                  />
                </CardContent>
              </Card>
            )}
            {/* Instagram link (not in header) */}
            {!editMode.isEditMode && platforms.instagram && (
              <Card>
                <CardContent className="p-4">
                  <a
                    href={platforms.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackStore.contactClicked({
                        platform: "instagram",
                        surface: "store_sidebar",
                        ...storeTrackingProps,
                      })
                    }
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-pink-50 transition-colors"
                  >
                    <div className="p-2 rounded-full bg-pink-100">
                      <Instagram className="w-5 h-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="font-medium font-arabic">إنستجرام</p>
                      <p className="text-sm text-gray-600">تابع المتجر</p>
                    </div>
                  </a>
                </CardContent>
              </Card>
            )}

            {/* Rating Card */}
            {/* <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 font-arabic mb-4">
                  تقييم المتجر
                </h3>

                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
                    <span className="text-3xl font-bold">
                      {store.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="text-gray-600 font-arabic">
                    من {store.totalReviews} تقييم
                  </p>

                  <div className="mt-4 space-y-2">
                    {[5, 4, 3, 2, 1].map((stars) => (
                      <div key={stars} className="flex items-center gap-2">
                        <span className="text-sm w-4">{stars}</span>
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-yellow-400 h-2 rounded-full"
                            style={{
                              width: `${Math.random() * 100}%`,
                            }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card> */}

            {/* Back Button */}
            {/* <Link to="/stores">
              <Button
                variant="outline"
                className="w-full flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                العودة للمتاجر
              </Button>
            </Link> */}
          </div>
        </div>

        {/* Top Products Section (edit mode only — view mode has ProductSamplesSlider above) */}
        {(editMode.editedStore.topProducts ?? []).length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 font-arabic mb-4">
              منتجات مميزة
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {editMode.editedStore.topProducts?.map((product, index) => {
                const handleDeleteProduct = () => {
                  const current = editMode.editedStore.topProducts ?? [];
                  editMode.updateField("topProducts", current.filter((_, i) => i !== index));
                };

                const cardContent = (
                  <>
                    {adminUi && (
                      <button
                        onClick={handleDeleteProduct}
                        className="absolute top-1 left-1 p-1 bg-white rounded-full shadow hover:bg-red-50 text-red-500"
                        title="حذف المنتج"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {product.imageUrl && (
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        className="w-full h-32 object-cover rounded-md mb-2"
                        loading="lazy"
                      />
                    )}
                    <p className="font-medium text-sm text-gray-900 line-clamp-2 font-arabic">
                      {product.name}
                    </p>
                    {product.nameEn && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {product.nameEn}
                      </p>
                    )}
                    {product.price && (
                      <p className="text-sm font-semibold text-blue-600 mt-1">
                        {product.price}
                      </p>
                    )}
                  </>
                );

                return (
                  <div
                    key={index}
                    className="relative border rounded-lg p-3 hover:shadow-md transition-shadow"
                  >
                    {cardContent}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      )} {/* end editMode.isEditMode container */}

      {/* ── Reviews Section — always rendered ── */}
      <div
        className="mx-auto max-w-[1400px] scroll-mt-28 px-4 pb-8 lg:px-10 lg:pb-12"
        ref={reviewsSectionRef}
      >
        {/* Public trust-check link (D-13): same copy on every store, SSR-rendered, not adminUi-gated */}
        <div className="mb-4"><BlogTrustLink surface="store_page" store={store} /></div>
        <ReviewsSection
          storeId={store._id}
          storeName={store.name}
          storeLogoUrl={store.logoUrl}
          categorySlug={store.category?.slug}
          initialReviewsData={initialReviewsData}
          storeStatus={store.status}
          storeSlug={store.slug}
          trackingProps={storeTrackingProps}
        />
      </div>

      {/* ── Similar stores — mobile-only rail experiment (quick-260815-dhs) ── */}
      {!editMode.isEditMode && similarStores.length > 0 && (
        <div
          id="similar-stores"
          className="lg:hidden mx-auto max-w-[1400px] scroll-mt-28 px-4 pb-8"
        >
          <h2
            style={{
              fontFamily: "var(--ys-font)",
              fontWeight: 700,
              fontSize: 20,
              color: "var(--ys-ink)",
              margin: "0 0 14px 0",
            }}
          >
            متاجر مشابهة
          </h2>
          <div className="flex flex-nowrap gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
            {similarStores.map((similarStore) => (
              <div
                key={similarStore._id}
                className="w-[72vw] max-w-[300px] shrink-0 snap-start"
              >
                <StoreListCard store={similarStore} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!isReviewMode && <Footer variant="minimal" />}
    </div>
  );
};

export default Store;
